// «Legg til eller endre oppgaver» under Gjøremål på startsiden (bare for leder, som resten av
// lederfunksjonene: et skille i menyen, ikke tilgangskontroll — se employeeAccess.js).
//
//   Faste oppgaver  /dailytasks  – tekst; legg til, endre, slett
//   Egne oppgaver   /customtasks – tekst og frist; legg til, endre, slett (bare de som ikke er gjort)
import React, { useEffect, useId, useState } from 'react';
import Ikon from './Ikon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// yyyy-mm-dd i lokal tid, til <input type="date">.
export const datoVerdi = (dato) => {
  const d = dato ? new Date(dato) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const felt = 'min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30';
const hovedknapp = 'shrink-0 rounded-lg bg-indigo-700 px-3 py-1.5 font-semibold text-white hover:bg-indigo-800 disabled:opacity-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';
const slettknapp = 'shrink-0 rounded-lg border border-rose-300 bg-white px-3 py-1.5 font-semibold text-rose-800 hover:bg-rose-50 disabled:opacity-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-600/40';

function RedigerRad({ oppgave, medFrist, opptatt, onLagre, onSlett }) {
  const id = useId();
  const [tekst, setTekst] = useState(oppgave.task);
  const [frist, setFrist] = useState(datoVerdi(oppgave.dueDate));
  useEffect(() => { setTekst(oppgave.task); setFrist(datoVerdi(oppgave.dueDate)); }, [oppgave.task, oppgave.dueDate]);

  const endret = tekst.trim() !== oppgave.task || (medFrist && frist !== datoVerdi(oppgave.dueDate));
  const lagre = (e) => {
    e.preventDefault();
    if (endret && tekst.trim()) onLagre(oppgave, { task: tekst.trim(), ...(medFrist && { dueDate: frist }) });
  };

  return (
    <li>
      <form onSubmit={lagre} className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-slate-200 bg-white p-2">
        <label htmlFor={`${id}-tekst`} className="sr-only">Oppgave</label>
        {/* Med frist: teksten på egen linje, så dato og knapper får plass under. */}
        <input id={`${id}-tekst`} value={tekst} maxLength={200} onChange={(e) => setTekst(e.target.value)} className={`${felt} ${medFrist ? 'w-full' : 'flex-1 basis-48'}`} />
        {medFrist && (
          <>
            <label htmlFor={`${id}-frist`} className="sr-only">Frist for «{oppgave.task}»</label>
            <input id={`${id}-frist`} type="date" value={frist} onChange={(e) => setFrist(e.target.value)} required className={`${felt} flex-1`} />
          </>
        )}
        <button type="submit" disabled={!endret || !tekst.trim() || opptatt} className={hovedknapp}>Lagre</button>
        <button type="button" onClick={() => onSlett(oppgave)} disabled={opptatt} aria-label={`Slett «${oppgave.task}»`} className={slettknapp}>
          Slett
        </button>
      </form>
    </li>
  );
}

function NyOppgave({ etikett, medFrist, opptatt, onLeggTil }) {
  const id = useId();
  const [tekst, setTekst] = useState('');
  const [frist, setFrist] = useState(datoVerdi());
  const send = async (e) => {
    e.preventDefault();
    if (!tekst.trim()) return;
    if (await onLeggTil({ task: tekst.trim(), ...(medFrist && { dueDate: frist }) })) {
      setTekst('');
      setFrist(datoVerdi());
    }
  };
  return (
    <form onSubmit={send} className="mt-3 rounded-xl border-2 border-dashed border-indigo-300 bg-white p-2.5">
      <label htmlFor={`${id}-tekst`} className="mb-1 block font-semibold text-slate-800">{etikett}</label>
      <div className="flex flex-wrap items-center gap-2">
        <input id={`${id}-tekst`} value={tekst} maxLength={200} onChange={(e) => setTekst(e.target.value)} className={`${felt} flex-1 basis-48`} />
        {medFrist && (
          <>
            <label htmlFor={`${id}-frist`} className="sr-only">Frist</label>
            <input id={`${id}-frist`} type="date" value={frist} onChange={(e) => setFrist(e.target.value)} required className={felt} />
          </>
        )}
        <button type="submit" disabled={!tekst.trim() || opptatt} className={`${hovedknapp} inline-flex items-center gap-2`}>
          <Ikon navn="pluss" className="w-5 h-5" /> Legg til
        </button>
      </div>
    </form>
  );
}

export default function GjoremalRediger({ faste, egne, butikk, onEndret }) {
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState('');
  const [melding, setMelding] = useState('');

  // Ett kall om gangen; svarer true når det gikk.
  const kall = async (sti, metode, body, ferdigTekst) => {
    setFeil('');
    setOpptatt(true);
    try {
      const r = await fetch(`${API_BASE_URL}/${sti}`, {
        method: metode,
        headers: { 'Content-Type': 'application/json' },
        ...(body && { body: JSON.stringify(body) }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setMelding(ferdigTekst);
      await onEndret();
      return true;
    } catch (e) {
      setFeil('Kunne ikke lagre endringen. Prøv igjen.');
      return false;
    } finally {
      setOpptatt(false);
    }
  };

  const slett = (sti, oppgave) => {
    if (window.confirm(`Slette «${oppgave.task}»?`)) kall(`${sti}/${oppgave._id}`, 'DELETE', null, `«${oppgave.task}» er slettet.`);
  };

  const apneEgne = egne.filter((o) => !o.dateCompleted).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  return (
    <div className="space-y-4">
      {feil && <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-amber-900">{feil}</p>}
      <p role="status" className="sr-only">{melding}</p>
      <div className="grid gap-3 lg:grid-cols-2">
        <section aria-label="Endre faste oppgaver" className="min-w-0">
          <h4 className="mb-2 font-bold text-slate-900">Faste oppgaver</h4>
          <p className="mb-3 text-sm text-slate-600">Kommer tilbake hver dag.</p>
          <ul className="space-y-2">
            {faste.map((o) => (
              <RedigerRad
                key={o._id}
                oppgave={o}
                opptatt={opptatt}
                onLagre={(op, endring) => kall(`dailytasks/${op._id}`, 'PATCH', endring, 'Endringen er lagret.')}
                onSlett={(op) => slett('dailytasks', op)}
              />
            ))}
          </ul>
          <NyOppgave
            etikett="Ny fast oppgave"
            opptatt={opptatt}
            onLeggTil={(ny) => kall('dailytasks', 'POST', { ...ny, store: butikk }, `«${ny.task}» er lagt til.`)}
          />
        </section>

        <section aria-label="Endre oppgaver med frist" className="min-w-0">
          <h4 className="mb-2 font-bold text-slate-900">Oppgaver med frist</h4>
          <p className="mb-3 text-sm text-slate-600">Vises under «Dagens oppgaver» fra fristen og til de er gjort.</p>
          {apneEgne.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 px-3 py-1.5 text-slate-600">Ingen oppgaver med frist.</p>
          ) : (
            <ul className="space-y-2">
              {apneEgne.map((o) => (
                <RedigerRad
                  key={o._id}
                  oppgave={o}
                  medFrist
                  opptatt={opptatt}
                  onLagre={(op, endring) => kall(`customtasks/${op._id}`, 'PATCH', endring, 'Endringen er lagret.')}
                  onSlett={(op) => slett('customtasks', op)}
                />
              ))}
            </ul>
          )}
          <NyOppgave
            etikett="Ny oppgave med frist"
            medFrist
            opptatt={opptatt}
            onLeggTil={(ny) => kall('customtasks', 'POST', { ...ny, store: butikk }, `«${ny.task}» er lagt til.`)}
          />
        </section>
      </div>
    </div>
  );
}
